import {
  FiActivity,
  FiAlignLeft,
  FiCalendar,
  FiCheckSquare,
  FiEdit3,
  FiHash,
  FiInfo,
  FiList,
  FiMapPin,
  FiToggleRight,
  FiType,
} from "react-icons/fi";
import { GiTooth } from "react-icons/gi";
import { TbDental } from "react-icons/tb";

import { QuestionType } from "@/types/forms/question";

import type { FieldTypeOption } from "./types";

export const FORM_FIELD_TYPES: FieldTypeOption[] = [
  {
    type: QuestionType.TEXT,
    label: "Short Text",
    description: "Single line text input",
    icon: FiType,
  },
  {
    type: QuestionType.TEXT_AREA,
    label: "Long Text",
    description: "Multi-line text area",
    icon: FiAlignLeft,
  },
  {
    type: QuestionType.NUMBER,
    label: "Number",
    description: "Numeric input",
    icon: FiHash,
  },
  {
    type: QuestionType.DATE,
    label: "Date",
    description: "Date picker",
    icon: FiCalendar,
  },
  {
    type: QuestionType.TOGGLE,
    label: "Yes/No Toggle",
    description: "Boolean switch",
    icon: FiToggleRight,
  },
  {
    type: QuestionType.SINGLE_SELECT,
    label: "Single Choice",
    description: "Select one option",
    icon: FiCheckSquare,
  },
  {
    type: QuestionType.MULTI_SELECT,
    label: "Multiple Choice",
    description: "Select multiple options",
    icon: FiList,
  },
];

// Display-only and signature field types surfaced in the note-template editor.
// SIGNATURE captures a drawn signature (e.g. the provider signing off on the
// note); DISCLAIMER/STATEMENT render static text on the note and never collect
// an answer.
export const NOTE_DISPLAY_FIELD_TYPES: FieldTypeOption[] = [
  {
    type: QuestionType.SIGNATURE,
    label: "Signature",
    description: "Drawn signature (e.g. provider sign-off)",
    icon: FiEdit3,
  },
  {
    type: QuestionType.PROVIDER_SIGNATURE,
    label: "Provider signature",
    description: "Stamp a provider's adopted signature",
    icon: FiEdit3,
  },
  {
    type: QuestionType.DISCLAIMER,
    label: "Disclaimer / Display Text",
    description: "Static text block shown on the note",
    icon: FiInfo,
  },
  {
    type: QuestionType.STATEMENT,
    label: "Statement",
    description: "Static bulleted list shown on the note",
    icon: FiList,
  },
];

// Question types available for editing in note templates. Dental types are
// excluded here and added via the DENTAL module gate (see useNoteQuestionTypes).
export const NOTE_QUESTION_TYPES: QuestionType[] = [
  QuestionType.TEXT,
  QuestionType.TEXT_AREA,
  QuestionType.NUMBER,
  QuestionType.DATE,
  QuestionType.TOGGLE,
  QuestionType.SINGLE_SELECT,
  QuestionType.RADIAL_SINGLE_SELECT,
  QuestionType.MULTI_SELECT,
  QuestionType.RADIAL_MULTI_SELECT,
];

// Dental chart-style types are clinician-only and gated by the DENTAL module.
export const DENTAL_QUESTION_TYPES: QuestionType[] = [
  QuestionType.DENTAL_CHART,
  QuestionType.PERIO_CHART,
  QuestionType.OCCLUSION_EXAM,
];

// Field-type options surfaced in the note-template editor for the new types.
export const DENTAL_FIELD_TYPE_OPTIONS: FieldTypeOption[] = [
  {
    type: QuestionType.DENTAL_CHART,
    label: "Dental Chart",
    description: "Tooth-by-tooth and per-surface restorative chart",
    icon: GiTooth,
  },
  {
    type: QuestionType.PERIO_CHART,
    label: "Perio Chart",
    description: "6-point pocket depths, bleeding, mobility, furcation",
    icon: TbDental,
  },
  {
    type: QuestionType.OCCLUSION_EXAM,
    label: "Occlusion & TMJ",
    description: "Angle's class, overjet/overbite, TMJ findings",
    icon: FiActivity,
  },
];

/**
 * Answer types offered for a question bound to a patient health field.
 *
 * Excludes TOGGLE, NUMBER and DATE. Health fields hold a list of strings
 * (`patient_dynamic_field_value`, one row per value), and none of these three can
 * represent that:
 *
 * - TOGGLE answers as a bool, which stringifies to a chart row named "True".
 * - NUMBER and DATE are typed `float` / ISO string by
 *   NOTE_TEMPLATE_QUESTION_TYPE_MAP, so a patient with several rows can only ever
 *   have one of them survive the round trip.
 *
 * The selects stay: "Smoking status: never / former / current" bound to Social
 * History is genuinely one value. This matches HISTORY_MAPPING_AI_QUESTION_TYPES,
 * which already drew the same line for AI-proposed types.
 */
export const HEALTH_DATA_QUESTION_TYPES = NOTE_QUESTION_TYPES.filter(
  (t) =>
    t !== QuestionType.TOGGLE &&
    t !== QuestionType.NUMBER &&
    t !== QuestionType.DATE,
);

/**
 * Answer types an AI assistant may propose for a patient-history mapping
 * (Medical History, Family History, ...).
 *
 * Now the same set as HEALTH_DATA_QUESTION_TYPES. This list used to be the
 * narrower of the two, on the reasoning that a person choosing NUMBER or DATE for
 * "Allergies" was merely odd while a model emitting one was bad output. That
 * reasoning predated knowing the binding silently deletes chart rows: a health
 * field holds one row per value, and neither type can carry more than one through
 * a note round trip. So the human picker was narrowed to match rather than this
 * one widened, and `question-mapping-defaults.test.ts` pins them equal.
 *
 * Kept as a separate constant because the two answer different questions — what a
 * person may pick versus what an imported/generated form operation may contain —
 * and could diverge again.
 */
export const HISTORY_MAPPING_AI_QUESTION_TYPES: QuestionType[] = [
  QuestionType.TEXT,
  QuestionType.TEXT_AREA,
  QuestionType.SINGLE_SELECT,
  QuestionType.RADIAL_SINGLE_SELECT,
  QuestionType.MULTI_SELECT,
  QuestionType.RADIAL_MULTI_SELECT,
];

/**
 * Answer types offered when editing a patient-history mapped question.
 *
 * Includes TAG, which the picker would otherwise not list even though existing
 * questions use it: `MedicalHistoryBlock.get_questions()` (decoda/schemas/forms/
 * block.py) hydrates Allergies, Family History, Medical History and
 * Prescriptions as TAG. Without it, opening one of those shows an empty
 * selection and there is no way back to a tag input after changing the type.
 */
export const HISTORY_MAPPING_QUESTION_TYPES: QuestionType[] = [
  ...HEALTH_DATA_QUESTION_TYPES,
  QuestionType.TAG,
];

/**
 * Display-only question types. These render static content from the question's
 * own config and never collect a NoteAnswer, so renderers must show them even
 * when no answer exists.
 */
export const DISPLAY_ONLY_QUESTION_TYPES = new Set<QuestionType>([
  QuestionType.DISCLAIMER,
  QuestionType.STATEMENT,
]);

/** Question types that require at least one option to be valid */
export const SELECT_QUESTION_TYPES = new Set<QuestionType>([
  QuestionType.SINGLE_SELECT,
  QuestionType.RADIAL_SINGLE_SELECT,
  QuestionType.MULTI_SELECT,
  QuestionType.RADIAL_MULTI_SELECT,
]);

/** Select types that take one answer, and so at most one default option */
export const SINGLE_SELECT_TYPES = new Set<QuestionType>([
  QuestionType.SINGLE_SELECT,
  QuestionType.RADIAL_SINGLE_SELECT,
]);

/**
 * Question types that cannot be marked as "required" unless they have at least
 * one conditional display rule. These types are informational/display-only and
 * making them "required" without conditions doesn't make sense semantically.
 */
export const REQUIRES_CONDITIONS_WHEN_REQUIRED_TYPES = new Set<QuestionType>([
  QuestionType.DISCLAIMER,
  QuestionType.STATEMENT,
]);

// Question types a public lead form may hold. Mirrors LEAD_QUESTION_TYPES in
// decoda/services/forms/lead_form_rules.py, which refuses anything else on save.
export const LEAD_FORM_QUESTION_TYPES: ReadonlySet<QuestionType> = new Set([
  QuestionType.TEXT,
  QuestionType.TEXT_AREA,
  QuestionType.NUMBER,
  QuestionType.DATE,
  QuestionType.EMAIL,
  QuestionType.PHONE_NUMBER,
  QuestionType.ADDRESS,
  QuestionType.SINGLE_SELECT,
  QuestionType.RADIAL_SINGLE_SELECT,
  QuestionType.MULTI_SELECT,
  QuestionType.RADIAL_MULTI_SELECT,
  QuestionType.TOGGLE,
  QuestionType.LOCATION,
  QuestionType.CHECKBOX,
  QuestionType.DISCLAIMER,
  QuestionType.STATEMENT,
]);

export const LOCATION_FIELD_TYPE: FieldTypeOption = {
  type: QuestionType.LOCATION,
  label: "Location",
  description: "Pick one of your in-person locations",
  icon: FiMapPin,
};

/**
 * Types the AI scribe never fills, whatever the template says. Mirrors
 * `AI_EXCLUDED_QUESTION_TYPES` in the scribe backend, plus attachments, which
 * the provider draws on. The "Provider fills this in" switch is pointless here.
 */
export const AI_NEVER_FILLS_QUESTION_TYPES: ReadonlySet<QuestionType> = new Set(
  [
    QuestionType.SIGNATURE,
    QuestionType.PROVIDER_SIGNATURE,
    QuestionType.DISCLAIMER,
    QuestionType.STATEMENT,
    QuestionType.SUPPLIES_USED,
    QuestionType.REBOOKING,
    QuestionType.SCRIBE_ATTACHMENTS,
  ],
);

/**
 * Types whose answer shape is fixed, so the editor offers no Answer Type picker
 * for them, only the Required switch.
 */
export const FIXED_ANSWER_QUESTION_TYPES: ReadonlySet<QuestionType> = new Set([
  QuestionType.SCRIBE_ATTACHMENTS,
  QuestionType.BODY_MAP,
  QuestionType.SOLD_ITEMS,
  QuestionType.SUPPLIES_USED,
  QuestionType.REBOOKING,
  QuestionType.GFE_CLEARANCE,
  QuestionType.TABLE,
  QuestionType.LOCATION,
]);
